// countryProductsLoader.js - Utility to load country-specific product data
import { getUserCountry, getUserCountrySync } from '../services/apiService';
import { normalizeCountryName, DEFAULT_COUNTRY } from '../services/countryService';

//------------------------------------------------
// CONSTANTS AND CONFIGURATION
//------------------------------------------------

/**
 * Map of country codes to their data files
 * This makes it easy to expand support for additional countries
 */
const COUNTRY_PRODUCT_FILES = {
  'Spain': 'spain.js',
  'Estonia': 'estonia.js',
  'Latvia': 'latvia.js',
  'Lithuania': 'lithuania.js',
  'Germany': 'germany.js',
  'France': 'france.js',
  'UK': 'uk.js',
  'United Kingdom': 'uk.js',
  'USA': 'usa.js',
  'Italy': 'italy.js',
  'Portugal': 'portugal.js',
  'Greece': 'greece.js',
  'Ireland': 'ireland.js',
  'Canada': 'canada.js',
  'Hungary': 'hungary.js'
};

/**
 * Cache for loaded product data
 * Prevents unnecessary reloading of the same data
 * @type {Object.<string, Array>}
 */
const productCache = {};
const schemaCache = {};

/**
 * Normalize and validate country name
 */
const normalizeAndValidateCountry = (country) => {
  if (!country) return DEFAULT_COUNTRY;
  
  // Normalize the country name
  const normalized = normalizeCountryName(country);
  
  // Check if normalized country is in our file mapping
  if (COUNTRY_PRODUCT_FILES[normalized]) {
    return normalized;
  }
  
  console.warn(`Country "${country}" (normalized to "${normalized}") not found in product mapping, using default ${DEFAULT_COUNTRY}`);
  return DEFAULT_COUNTRY;
};

//------------------------------------------------
// DATA LOADING FUNCTIONS
//------------------------------------------------

/**
 * Load product data for a specific country
 *
 * @param {String} country - Country name (optional, defaults to detected country)
 * @returns {Promise<Array>} Product database for the country
 */
export const getCountryProducts = async (country) => {
  try {
    const rawCountry = country || await getUserCountry() || DEFAULT_COUNTRY;
    const effectiveCountry = normalizeAndValidateCountry(rawCountry);
    
    console.log(`Loading products for country: ${rawCountry} (normalized to ${effectiveCountry})`);
    
    // Check cache
    if (productCache[effectiveCountry]) {
      return productCache[effectiveCountry];
    }

    // Find the country file
    const filename = COUNTRY_PRODUCT_FILES[effectiveCountry];
    if (!filename) {
      console.warn(`No product database file configured for country: ${effectiveCountry}`);
      if (effectiveCountry !== DEFAULT_COUNTRY && COUNTRY_PRODUCT_FILES[DEFAULT_COUNTRY]) {
        console.log(`Falling back to ${DEFAULT_COUNTRY} product database`);
        return getCountryProducts(DEFAULT_COUNTRY);
      }
      return [];
    }


    // For Node or bundlers that support dynamic import:
    try {
      console.log(`Loading product file: ${filename} for ${effectiveCountry}`);
      const module = await import(`../utils/prices/${filename}`);
      let data = module.default || module;
      
      // Handle new schema format (with schema and products properties)
      if (data && data.products && Array.isArray(data.products)) {
        console.log(`Loaded ${data.products.length} products from ${effectiveCountry} database with schema version ${data.schema?.version || 'unknown'}`);
        
        // Cache the schema if it exists
        if (data.schema) {
          schemaCache[effectiveCountry] = data.schema;
        }
        
        // Transform standardSizes to units for each product
        const transformedProducts = data.products.map(product => {
          // Create a new product object with the same properties
          const transformedProduct = { ...product };
          
          // If the product has standardSizes but no units, transform standardSizes to units
          if (transformedProduct.standardSizes && !transformedProduct.units) {
            transformedProduct.units = transformedProduct.standardSizes.map(sizeObj => {
              const { amount, ...rest } = sizeObj;
              return { ...rest, size: amount };
            });
          }
          
          return transformedProduct;
        });
        
        productCache[effectiveCountry] = transformedProducts;
        return transformedProducts;
      } 
      // Handle legacy format (just an array)
      else if (Array.isArray(data)) {
        console.log(`Loaded ${data.length} products from ${effectiveCountry} database (legacy format)`);
        
        // Transform standardSizes to units for each product
        const transformedProducts = data.map(product => {
          // Create a new product object with the same properties
          const transformedProduct = { ...product };
          
          // If the product has standardSizes but no units, transform standardSizes to units
          if (transformedProduct.standardSizes && !transformedProduct.units) {
            transformedProduct.units = transformedProduct.standardSizes.map(sizeObj => {
              const { amount, ...rest } = sizeObj;
              return { ...rest, size: amount };
            });
          }
          
          return transformedProduct;
        });
        
        productCache[effectiveCountry] = transformedProducts;
        return transformedProducts;
      } 
      // Unknown format
      else {
        console.error(`Unknown product format in ${filename}`, data);
        return [];
      }
    } catch (importError) {
      console.error(`Error importing product database for ${effectiveCountry}:`, importError);
      
      // Fall back to default country if available
      if (effectiveCountry !== DEFAULT_COUNTRY && COUNTRY_PRODUCT_FILES[DEFAULT_COUNTRY]) {
        console.log(`Import error, falling back to ${DEFAULT_COUNTRY} product database`);
        return getCountryProducts(DEFAULT_COUNTRY);
      }
      return [];
    }
  } catch (error) {
    console.error(`Error loading product database for ${country}:`, error);
    return [];
  }
};

/**
 * Get schema information from the country product file
 * 
 * @param {String} country - Country name (optional, defaults to detected country)
 * @returns {Promise<Object>} Schema object or null
 */
export const getCountrySchema = async (country) => {
  try {
    const rawCountry = country || await getUserCountry() || DEFAULT_COUNTRY;
    const effectiveCountry = normalizeAndValidateCountry(rawCountry);
    
    console.log(`Getting schema for country: ${rawCountry} (normalized to ${effectiveCountry})`);
    
    // Check cache
    if (schemaCache[effectiveCountry]) {
      return schemaCache[effectiveCountry];
    }
    
    // Find the country file
    const filename = COUNTRY_PRODUCT_FILES[effectiveCountry];
    if (!filename) {
      console.warn(`No product database file configured for country: ${effectiveCountry}`);
      if (effectiveCountry !== DEFAULT_COUNTRY && COUNTRY_PRODUCT_FILES[DEFAULT_COUNTRY]) {
        console.log(`Falling back to ${DEFAULT_COUNTRY} schema`);
        return getCountrySchema(DEFAULT_COUNTRY);
      }
      return null;
    }

    // For Node or bundlers that support dynamic import:
    try {
      console.log(`Loading schema from file: ${filename} for ${effectiveCountry}`);
      const module = await import(`../utils/prices/${filename}`);
      let data = module.default || module;
      
      // Extract and cache schema
      if (data && data.schema) {
        console.log(`Found schema version ${data.schema.version} for ${effectiveCountry}`);
        schemaCache[effectiveCountry] = data.schema;
        return data.schema;
      } 
      // Handle legacy format (no schema)
      else {
        console.log(`No schema found in ${filename} for ${effectiveCountry}`);
        return null;
      }
    } catch (importError) {
      console.error(`Error importing schema for ${effectiveCountry}:`, importError);
      
      // Fall back to default country if available
      if (effectiveCountry !== DEFAULT_COUNTRY && COUNTRY_PRODUCT_FILES[DEFAULT_COUNTRY]) {
        console.log(`Import error, falling back to ${DEFAULT_COUNTRY} schema`);
        return getCountrySchema(DEFAULT_COUNTRY);
      }
      return null;
    }
  } catch (error) {
    console.error(`Error loading schema for ${country}:`, error);
    return null;
  }
};

/**
 * Clear the product cache (useful for testing)
 */
export const clearProductCache = () => {
  Object.keys(productCache).forEach(key => {
    delete productCache[key];
  });
};

/**
 * Check if a country is supported
 * 
 * @param {String} country - Country name to check
 * @returns {Boolean} Whether the country is supported
 */
export const isCountrySupported = (country) => {
  return Object.prototype.hasOwnProperty.call(COUNTRY_PRODUCT_FILES, country);
};

/**
 * Get a list of all supported countries
 * 
 * @returns {Array<String>} List of supported country names
 */
export const getSupportedCountries = () => {
  return Object.keys(COUNTRY_PRODUCT_FILES);
};